"use client";
import React, { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import {
  SignedIn,
  SignedOut,
  RedirectToSignIn,
} from "@daveyplate/better-auth-ui";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import Spinner from "~/components/ui/spinner";

import { FiRefreshCw } from "react-icons/fi";
import { BsQuestionCircleFill } from "react-icons/bs";
import { GiCheckMark } from "react-icons/gi";
import { IoHourglassOutline } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";

import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import { useToast } from "~/hooks/use-toast";
import { useUserInfo } from "~/components/user-info-provider";
import {
  type OpeningListItem,
  type SessionTaskAttempt,
  SessionTaskAttemptResultEnum,
  SessionTaskAttemptStateEnum,
} from "~/lib/objects";
import { findProblemSetTargetProblem } from "~/utils/findFirstTargetProblem";
import { smartAnsiCompareText } from "~/lib/textComparison/smartAnsiCompareText";
import { getRatingDeltaText, getTaskStateIcon } from "~/components/utils";
import { formatDualPercent } from "~/lib/formatPercent";
import { TbAward } from "react-icons/tb";
import DetailedStatsDialog from "~/components/ui/detailed-stats-dialog";
import DownloadSolutionsDialog from "~/components/ui/download-solutions-dialog";
import ProblemSetActions from "~/features/mistakeReview/ProblemSetActions";
import { ProblemSetVisibilityEnum } from "~/lib/objects";
import {
  computeDetailedStatistics,
  createEmptyDetailedStatistics,
  convertProblemItemsToAttempts,
} from "~/lib/detailedStats";
import { buildDownloadRows } from "~/lib/downloadOptions";
import { computeMaxRatingPercent } from "~/lib/computeProblemSetStats";
import { AiOutlineDownload } from "react-icons/ai";
import OpeningList from "~/components/openings/opening-list";
import { useOpeningTitleQueryStore } from "~/components/openings/use-opening-title-query-store";
import HomeOpeningGroupControl from "~/components/openings/home-opening-group-control";
import {
  persistHomeOpeningGroupSelection,
  readPendingHomeOpeningGroupSelection,
  resolveHomeOpeningGroupSelection,
  type HomeOpeningGroupSelection,
} from "~/components/openings/home-opening-group-selection";
import type {
  OpeningGroupListItem,
  OpeningScopedListResponse,
} from "~/lib/openingGroups";
import { filterByWordPrefixes } from "~/lib/search/word-prefix-search";
import GameAnalysisList from "~/features/gameAnalysis/GameAnalysisList";
import {
  formatRatingByMovesCountRetryDisplay,
  type RatingByMovesCountDisplay,
} from "~/features/ratingByMovesCountDisplay/model";

export interface ProblemItem {
  name: string;
  problemId: number;
  problemOrder: number;
  roundName: string | null;
  rating: number | null;
  result: SessionTaskAttemptResultEnum;
  state: SessionTaskAttemptStateEnum;
  solutionEdited: boolean;
  hintUsed: boolean;
  finalExternalHint?: boolean;
  ratingDelta: number;
  retryNumber: number;
  ratingByMovesCountDisplay?: RatingByMovesCountDisplay;
}

export interface ProblemSet {
  problemSetId: number;
  name: string;
  visible: boolean;
  visibility?: ProblemSetVisibilityEnum;
  problemsCount: number;
  solvedCount: number;
  winCount: number;
  drawCount: number;
  lossCount: number;
  pendingCount: number;
  state: SessionTaskAttemptResultEnum;
  ratingDelta: number;
  retryNumber: number;
  maxRatingPercent: number;
  maxRatingPercentSolved: number;
  problems: ProblemItem[];
}

export default function HomePage() {
  const router = useRouter();
  const routerRef = React.useRef(router);
  routerRef.current = router;
  const replaceRoute = useCallback((url: string) => {
    routerRef.current.replace(url, { scroll: false });
  }, []);
  const { toast } = useToast();
  const [selectedPage, setSelectedPage] =
    useState<HomeOpeningGroupSelection | null>(null);
  const [sectionRestored, setSectionRestored] = useState(false);
  const selectedPageRef = React.useRef<HomeOpeningGroupSelection | null>(null);
  const selectionGenerationRef = React.useRef(0);
  const openingCacheRef = React.useRef(new Map<number, OpeningListItem[]>());
  const openingsRequestRef = React.useRef(
    new Map<number, Promise<OpeningScopedListResponse | null>>(),
  );
  const [openingGroups, setOpeningGroups] = useState<OpeningGroupListItem[]>(
    [],
  );
  const [openingGroupsLoaded, setOpeningGroupsLoaded] = useState(false);
  const [sets, setSets] = useState<ProblemSet[]>([]);
  const [problemSetsLoaded, setProblemSetsLoaded] = useState(false);
  const [openings, setOpenings] = useState<OpeningListItem[]>([]);
  const [filteredSets, setFilteredSets] = useState<ProblemSet[]>([]);
  const [searchName, setSearchName] = useState("");
  const [statusFilter] = useState<string | undefined>(undefined);
  const [sortKey, setSortKey] = useState<keyof ProblemSet | "default">(
    "default",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [expandedSets, setExpandedSets] = useState<Set<number>>(new Set());
  const [isStatsDialogOpen, setIsStatsDialogOpen] = useState(false);
  const [selectedProblemSetForStats, setSelectedProblemSetForStats] =
    useState<ProblemSet | null>(null);
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [selectedProblemSetForDownload, setSelectedProblemSetForDownload] =
    useState<ProblemSet | null>(null);

  const { userInfo } = useUserInfo();
  const openingTitleQueryStore = useOpeningTitleQueryStore();

  const computedStatsForDialog = React.useMemo(() => {
    if (!selectedProblemSetForStats) {
      return createEmptyDetailedStatistics();
    }

    try {
      const lifeRating = userInfo?.lifeRating ?? 0;
      const mockAttempts: SessionTaskAttempt[] =
        selectedProblemSetForStats.problems.map((p) => ({
          attemptId: p.problemId,
          sessionId: 0,
          problemId: p.problemId,
          mainAttemptId: null,
          state: p.state,
          result: p.result,
          finalState: p.state,
          finalResult: p.result,
          hintUsed: Boolean(p.hintUsed),
          externalHint: false,
          internalHint: false,
          finalExternalHint: Boolean(p.finalExternalHint),
          finalInternalHint: false,
          optionalHintUsed: null,
          retryCountUsed: p.retryNumber ?? 0,
          durationSec: 0,
          score: 0,
          maxScore: 0,
          userMoves: null,
          pointsChange: p.ratingDelta ?? 0,
          taskRating: p.rating ?? 1000,
          strictMoves: null,
          questionResult: null,
          lockedMoves: null,
          solutionMoves: null,
          premoves: 0,
          premovesSAN: null,
          revealed: false,
          equivalentMove: null,
          skippedEquivalent: false,
          solveOrder: p.problemOrder,
          startedAt: null,
          autoHideStartedAt: null,
          timeControlStartedAt: null,
          timeControlMoveStartedAt: null,
          timeControlExpiredAt: null,
          lastUpdatedAt: null,
        }));
      const problems = selectedProblemSetForStats.problems.map((p) => ({
        problemId: p.problemId,
        name: p.name ?? undefined,
        rating: p.rating,
      }));
      return computeDetailedStatistics(mockAttempts, problems, { lifeRating });
    } catch {
      return createEmptyDetailedStatistics();
    }
  }, [selectedProblemSetForStats, userInfo?.lifeRating]);

  const computedRatingStats = React.useMemo(() => {
    if (!selectedProblemSetForStats) {
      return null;
    }

    const lifeRating = userInfo?.lifeRating ?? 0;
    if (lifeRating === 0) {
      return null;
    }

    try {
      const mappedProblems = selectedProblemSetForStats.problems.map((p) => ({
        problemId: p.problemId,
        rating: p.rating,
        ratingDelta: p.ratingDelta,
        result: p.result,
        finalExternalHint: Boolean(p.finalExternalHint),
        retryCountUsed: p.retryNumber,
      }));
      return computeMaxRatingPercent(mappedProblems, lifeRating);
    } catch {
      return null;
    }
  }, [selectedProblemSetForStats, userInfo?.lifeRating]);

  const fetchProblemSets = useCallback(async () => {
    try {
      const res = await fetch("/api/problem-sets");
      if (!res.ok) {
        const err = (await res.json()) as { message: string };
        toast({
          title: "Failed to load problem sets.",
          description: err.message ?? "Unknown error.",
          variant: "destructive",
        });
        return;
      }

      const data: ProblemSet[] = (await res.json()) as ProblemSet[];
      setSets(data);
      setFilteredSets(data);
      setProblemSetsLoaded(true);
    } catch (error) {
      console.error(error);
      toast({
        title: "Network error.",
        description: (error as Error).message,
        variant: "destructive",
      });
    }
  }, [toast]);

  const commitPageSelection = useCallback(
    (selection: HomeOpeningGroupSelection) => {
      selectionGenerationRef.current += 1;
      selectedPageRef.current = selection;
      setSelectedPage(selection);
      persistHomeOpeningGroupSelection(selection);

      if (selection.kind === "opening-group") {
        setOpenings(openingCacheRef.current.get(selection.problemSetId) ?? []);
      }
    },
    [],
  );

  const fetchOpeningGroups = useCallback(async (): Promise<
    OpeningGroupListItem[]
  > => {
    const response = await fetch("/api/opening-groups");
    if (!response.ok) {
      const error = (await response.json()) as { message?: string };
      throw new Error(error.message ?? "Unknown error.");
    }
    return (await response.json()) as OpeningGroupListItem[];
  }, []);

  useEffect(() => {
    let cancelled = false;
    const returnUrl = new URL(window.location.href);
    const isAnalysisReturn = returnUrl.searchParams.get("section") === "game-analysis";
    const pending = isAnalysisReturn
      ? { kind: "game-analysis" as const }
      : readPendingHomeOpeningGroupSelection();

    if (pending.kind === "tasks" || pending.kind === "game-analysis") {
      commitPageSelection(pending);
    }
    setSectionRestored(true);
    const generation = selectionGenerationRef.current;
    if (isAnalysisReturn) {
      returnUrl.searchParams.delete("section");
      replaceRoute(`${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`);
    }

    void (async () => {
      try {
        const groups = await fetchOpeningGroups();
        if (cancelled) return;
        setOpeningGroups(groups);
        setOpeningGroupsLoaded(true);

        if (
          pending.kind !== "tasks" &&
          pending.kind !== "game-analysis" &&
          selectionGenerationRef.current === generation
        ) {
          commitPageSelection(
            resolveHomeOpeningGroupSelection(pending, groups),
          );
        }
      } catch (error) {
        if (cancelled) return;
        setOpeningGroups([]);
        setOpeningGroupsLoaded(true);
        toast({
          title: "Failed to load opening groups.",
          description: (error as Error).message,
          variant: "destructive",
        });
        if (selectedPageRef.current?.kind !== "game-analysis") {
          commitPageSelection({ kind: "tasks" });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [commitPageSelection, fetchOpeningGroups, toast, replaceRoute]);

  const recoverUnavailableOpeningGroup = useCallback(
    async (generation: number) => {
      try {
        const groups = await fetchOpeningGroups();
        if (selectionGenerationRef.current !== generation) return;
        setOpeningGroups(groups);
        setOpeningGroupsLoaded(true);
        commitPageSelection(
          resolveHomeOpeningGroupSelection(
            { kind: "first-opening-group" },
            groups,
          ),
        );
      } catch (error) {
        if (selectionGenerationRef.current !== generation) return;
        toast({
          title: "Failed to load opening groups.",
          description: (error as Error).message,
          variant: "destructive",
        });
        commitPageSelection({ kind: "tasks" });
      }
    },
    [commitPageSelection, fetchOpeningGroups, toast],
  );

  const fetchOpenings = useCallback(
    async (problemSetId: number, generation: number) => {
      const cached = openingCacheRef.current.get(problemSetId);
      if (cached) {
        if (selectionGenerationRef.current === generation) setOpenings(cached);
        return;
      }

      let request = openingsRequestRef.current.get(problemSetId);
      if (!request) {
        request = (async () => {
          try {
            const response = await fetch(
              `/api/openings?problemSetId=${problemSetId}`,
            );
            if (!response.ok) {
              const error = (await response.json()) as { message?: string };
              toast({
                title: "Failed to load openings.",
                description: error.message ?? "Unknown error.",
                variant: "destructive",
              });
              return null;
            }
            return (await response.json()) as OpeningScopedListResponse;
          } catch (error) {
            console.error(error);
            toast({
              title: "Network error.",
              description: (error as Error).message,
              variant: "destructive",
            });
            return null;
          }
        })();
        openingsRequestRef.current.set(problemSetId, request);
        void request.finally(() => {
          if (openingsRequestRef.current.get(problemSetId) === request) {
            openingsRequestRef.current.delete(problemSetId);
          }
        });
      }

      const data = await request;
      if (!data) return;
      if (data.group === null) {
        if (selectionGenerationRef.current === generation) {
          await recoverUnavailableOpeningGroup(generation);
        }
        return;
      }

      openingCacheRef.current.set(problemSetId, data.openings);
      if (
        selectionGenerationRef.current === generation &&
        selectedPageRef.current?.kind === "opening-group" &&
        selectedPageRef.current.problemSetId === problemSetId
      ) {
        setOpenings(data.openings);
      }
    },
    [recoverUnavailableOpeningGroup, toast],
  );

  useEffect(() => {
    if (selectedPage?.kind === "tasks") {
      if (!problemSetsLoaded) void fetchProblemSets();
      return;
    }
    if (selectedPage?.kind === "opening-group") {
      void fetchOpenings(
        selectedPage.problemSetId,
        selectionGenerationRef.current,
      );
    }
  }, [selectedPage, problemSetsLoaded, fetchProblemSets, fetchOpenings]);

  useEffect(() => {
    let data = [...sets];
    if (searchName) {
      data = [...filterByWordPrefixes(data, searchName, (set) => set.name)];
    }
    if (statusFilter) {
      data = data.filter(
        (s) => s.state === (statusFilter as SessionTaskAttemptResultEnum),
      );
    }
    data.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "default") {
        if (
          (a.pendingCount > 0 && b.pendingCount > 0) ||
          a.pendingCount === b.pendingCount
        ) {
          cmp = smartAnsiCompareText(String(a.name), String(b.name));
        } else if (a.pendingCount > 0) {
          cmp = -1;
        } else if (b.pendingCount > 0) {
          cmp = 1;
        } else {
          cmp = smartAnsiCompareText(String(a.name), String(b.name));
        }
      } else if (sortKey === "name") {
        cmp = smartAnsiCompareText(String(a[sortKey]), String(b[sortKey]));
      } else if (sortKey === "pendingCount") {
        cmp = b[sortKey] - a[sortKey];
        if (cmp === 0) {
          cmp = smartAnsiCompareText(String(a.name), String(b.name));
        }
      } else {
        cmp = (a[sortKey] as number) - (b[sortKey] as number);
        if (cmp === 0) {
          cmp = smartAnsiCompareText(String(a.name), String(b.name));
        }
      }
      return sortOrder === "asc" ? cmp : -cmp;
    });
    setFilteredSets(data);
  }, [sets, searchName, statusFilter, sortKey, sortOrder]);

  const handleSort = (key: keyof ProblemSet) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortKey(key);
    setSortOrder("asc");
  };

  const toggleExpand = (id: number) => {
    setExpandedSets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getSortIcon = (columnKey: keyof ProblemSet) => {
    if (sortKey !== columnKey) {
      return null;
    }

    return sortOrder === "asc" ? (
      <ChevronUp className="ml-1 inline-block h-4 w-4" />
    ) : (
      <ChevronDown className="ml-1 inline-block h-4 w-4" />
    );
  };

  const handleProblemSetNameClick = useCallback(
    (set: ProblemSet) => {
      const targetProblem = findProblemSetTargetProblem(set.problems);

      if (targetProblem) {
        router.push(
          `/problem-sets/${set.problemSetId}/problems/${targetProblem.problemId}`,
        );
      }
    },
    [router],
  );

  const handleOpeningClick = useCallback(
    (opening: OpeningListItem) => {
      router.push(
        `/problem-sets/${opening.problemSetId}/problems/${opening.problemId}/viewer`,
      );
    },
    [router],
  );

  return (
    <>
      {sectionRestored && selectedPage?.kind !== "game-analysis" && (
        <SignedOut><RedirectToSignIn /></SignedOut>
      )}
      <div className="fixed inset-0 top-16 flex flex-col p-4">
        <SignedIn>
          <div
            data-testid="page-control-shell"
            className="mb-4 flex w-full justify-center"
          >
            <HomeOpeningGroupControl
              groups={openingGroups}
              value={selectedPage}
              onValueChange={commitPageSelection}
              disabled={!openingGroupsLoaded}
            />
          </div>

          {selectedPage === null ? (
            <div
              role="status"
              aria-label="Загрузка"
              className="flex min-h-0 flex-1 items-center justify-center"
            >
              <Spinner style={{ width: 40, height: 40 }} />
            </div>
          ) : selectedPage.kind === "tasks" ? (
            <>
              <div className="mb-4 flex flex-shrink-0 flex-wrap items-center gap-2">
                <Input
                  placeholder="Фильтр по названию"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                />
                <button
                  type="button"
                  aria-label="noop"
                  onClick={() => undefined}
                  className="ml-2"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-hidden rounded-md border">
                <div className="h-full overflow-auto">
                  <table className="w-full caption-bottom text-sm">
                    <thead className="sticky top-0 z-10 border-b bg-background">
                      <tr className="border-b">
                        <th
                          onClick={() => handleSort("name")}
                          className="h-10 cursor-pointer bg-background px-2 text-left align-middle font-medium text-muted-foreground"
                        >
                          Название{getSortIcon("name")}
                        </th>
                        <th
                          onClick={() => handleSort("state")}
                          className="h-10 cursor-pointer bg-background px-2 text-left align-middle font-medium text-muted-foreground"
                        >
                          <TbAward className="mr-2 inline-block" />
                          {getSortIcon("state")}
                        </th>
                        <th
                          onClick={() => handleSort("problemsCount")}
                          className="h-10 cursor-pointer bg-background px-2 text-left align-middle font-medium text-muted-foreground"
                        >
                          #{getSortIcon("problemsCount")}
                        </th>
                        <th
                          onClick={() => handleSort("winCount")}
                          className="h-10 cursor-pointer bg-background px-2 text-left align-middle font-medium text-muted-foreground"
                        >
                          <GiCheckMark
                            className="mr-2 inline-block"
                            color="green"
                          />
                          {getSortIcon("winCount")}
                        </th>
                        <th
                          onClick={() => handleSort("drawCount")}
                          className="h-10 cursor-pointer bg-background px-2 text-left align-middle font-medium text-muted-foreground"
                        >
                          <span className="mr-2 text-yellow-300">½ </span>
                          {getSortIcon("drawCount")}
                        </th>
                        <th
                          onClick={() => handleSort("lossCount")}
                          className="h-10 cursor-pointer bg-background px-2 text-left align-middle font-medium text-muted-foreground"
                        >
                          <RxCross2 className="mr-2 inline-block" color="red" />
                          {getSortIcon("lossCount")}
                        </th>
                        <th
                          onClick={() => handleSort("pendingCount")}
                          className="h-10 cursor-pointer bg-background px-2 text-left align-middle font-medium text-muted-foreground"
                        >
                          <IoHourglassOutline className="mr-2 inline-block" />
                          {getSortIcon("pendingCount")}
                        </th>
                        <th
                          onClick={() => handleSort("maxRatingPercent")}
                          className="h-10 cursor-pointer bg-background px-2 text-left align-middle font-medium text-muted-foreground"
                        >
                          % {getSortIcon("maxRatingPercent")}
                        </th>
                        <th
                          onClick={() => handleSort("retryNumber")}
                          className="h-10 cursor-pointer bg-background px-2 text-left align-middle font-medium text-muted-foreground"
                        >
                          <FiRefreshCw className="ml-0.5 inline-block text-pink-400" />
                          {getSortIcon("retryNumber")}
                        </th>
                        <th
                          className="h-10 bg-background px-2 text-left align-middle font-medium text-muted-foreground"
                          aria-label="Download solutions"
                        >
                          <BsQuestionCircleFill className="inline-block text-gray-400" />
                        </th>
                        <th
                          className="h-10 bg-background px-2 text-left align-middle font-medium text-muted-foreground"
                          aria-label="Statistics"
                        >
                          <AiOutlineDownload />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSets.map((set) => {
                        const statusIcon = getTaskStateIcon({
                          attemptResult:
                            set.state === SessionTaskAttemptResultEnum.PENDING
                              ? SessionTaskAttemptResultEnum.PENDING
                              : SessionTaskAttemptResultEnum.WIN,
                          isCurrentAttempt: false,
                          solutionEdited: false,
                          attemptState:
                            set.state === SessionTaskAttemptResultEnum.PENDING
                              ? SessionTaskAttemptStateEnum.IN_PROGRESS
                              : [
                                    SessionTaskAttemptResultEnum.WIN,
                                    SessionTaskAttemptResultEnum.DRAW,
                                    SessionTaskAttemptResultEnum.LOSS,
                                  ].includes(set.state)
                                ? SessionTaskAttemptStateEnum.DONE
                                : SessionTaskAttemptStateEnum.NOT_STARTED,
                          retryNumber: 0,
                          hintUsed: false,
                        });

                        return (
                          <React.Fragment key={set.problemSetId}>
                            <tr className="border-b text-lg transition-colors hover:bg-muted/50">
                              <td className="p-2 align-middle">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label={
                                    expandedSets.has(set.problemSetId)
                                      ? `Свернуть ${set.name}`
                                      : `Развернуть ${set.name}`
                                  }
                                  onClick={() => toggleExpand(set.problemSetId)}
                                >
                                  {expandedSets.has(set.problemSetId) ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                                {statusIcon}{" "}
                                <span
                                  data-testid={`problem-set-name-${set.problemSetId}`}
                                  className="cursor-pointer hover:underline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleProblemSetNameClick(set);
                                  }}
                                >
                                  {set.name}
                                </span>
                              </td>
                              <td className="p-2 align-middle">
                                {getRatingDeltaText(set.ratingDelta, true)}
                              </td>
                              <td className="p-2 align-middle">
                                {set.problemsCount}
                              </td>
                              <td className="p-2 align-middle">
                                {set.winCount > 0 ? set.winCount : "-"}
                              </td>
                              <td className="p-2 align-middle">
                                {set.drawCount > 0 ? set.drawCount : "-"}
                              </td>
                              <td className="p-2 align-middle">
                                {set.lossCount > 0 ? set.lossCount : "-"}
                              </td>
                              <td className="p-2 align-middle">
                                {set.pendingCount > 0 ? set.pendingCount : "-"}
                              </td>
                              <td className="p-2 align-middle">
                                {formatDualPercent(
                                  set.maxRatingPercent,
                                  set.maxRatingPercentSolved,
                                  set.solvedCount > 0,
                                )}
                              </td>
                              <td className="p-2 align-middle">
                                {set.retryNumber > 0 ? set.retryNumber : "-"}
                              </td>
                              <td className="p-2 align-middle">
                                <button
                                  data-testid={`stats-button-${set.problemSetId}`}
                                  className="rounded p-1 hover:bg-muted/50"
                                  aria-label={`Open statistics for ${set.name}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedProblemSetForStats(set);
                                    setIsStatsDialogOpen(true);
                                  }}
                                >
                                  <BsQuestionCircleFill className="text-gray-400 hover:text-gray-200" />
                                </button>
                              </td>
                              <td className="p-2 align-middle">
                                <ProblemSetActions
                                  source={{
                                    problemSetId: set.problemSetId,
                                    name: set.name,
                                    visibility: set.visibility ?? ProblemSetVisibilityEnum.PUBLIC,
                                  }}
                                  historyVersion={set.problems}
                                  onCreated={() => { void fetchProblemSets(); }}
                                  data-testid={`download-button-${set.problemSetId}`}
                                  className="rounded p-1 hover:bg-muted/50"
                                  aria-label={`Download solutions for ${set.name}`}
                                  onDownload={() => {
                                    setSelectedProblemSetForDownload(set);
                                    setIsDownloadDialogOpen(true);
                                  }}
                                />
                              </td>
                            </tr>
                            {expandedSets.has(set.problemSetId) &&
                              set.problems.map((problem) => {
                                const problemIcon = getTaskStateIcon({
                                  attemptResult: problem.result,
                                  isCurrentAttempt: false,
                                  solutionEdited:
                                    problem.state ===
                                    SessionTaskAttemptStateEnum.IN_PROGRESS,
                                  attemptState: problem.state,
                                  retryNumber: problem.retryNumber,
                                  hintUsed: false,
                                  externalHint:
                                    problem.finalExternalHint ?? false,
                                });

                                return (
                                  <tr
                                    key={problem.problemId}
                                    onClick={() =>
                                      router.push(
                                        `/problem-sets/${set.problemSetId}/problems/${problem.problemId}`,
                                      )
                                    }
                                    className="cursor-pointer border-b transition-colors hover:bg-muted/50"
                                  >
                                    <td className="p-2 pl-20 align-middle">
                                      {problemIcon} {problem.name}
                                    </td>
                                    <td className="p-2 align-middle">
                                      {getRatingDeltaText(problem.ratingDelta)}
                                    </td>
                                    <td className="p-2 align-middle" />
                                    <td className="p-2 align-middle" />
                                    <td className="p-2 align-middle" />
                                    <td className="p-2 align-middle" />
                                    <td className="p-2 align-middle" />
                                    <td className="p-2 align-middle" />
                                    <td className="p-2 align-middle">
                                      {formatRatingByMovesCountRetryDisplay(
                                        problem.retryNumber,
                                        problem.ratingByMovesCountDisplay,
                                      )}
                                    </td>
                                    <td className="p-2 align-middle" />
                                  </tr>
                                );
                              })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : selectedPage.kind === "opening-group" ? (
            <OpeningList
              key={selectedPage.problemSetId}
              problemSetId={selectedPage.problemSetId}
              openings={openings}
              loaded={openingCacheRef.current.has(selectedPage.problemSetId)}
              queryStore={openingTitleQueryStore}
              onOpeningClick={handleOpeningClick}
            />
          ) : null}
        </SignedIn>
        <GameAnalysisList selected={selectedPage?.kind === "game-analysis"} />
      </div>
      <SignedIn>
        <DetailedStatsDialog
          isOpen={isStatsDialogOpen}
          onClose={() => {
            setIsStatsDialogOpen(false);
            setSelectedProblemSetForStats(null);
          }}
          statistics={computedStatsForDialog}
          problemSetId={selectedProblemSetForStats?.problemSetId ?? 0}
          percentAll={
            computedRatingStats?.percent ??
            selectedProblemSetForStats?.maxRatingPercent ??
            0
          }
          percentSolved={
            computedRatingStats?.percentSolved ??
            selectedProblemSetForStats?.maxRatingPercentSolved ??
            0
          }
          scurr={computedRatingStats?.scurr ?? 0}
          smax={computedRatingStats?.smax ?? 0}
          smaxSolved={computedRatingStats?.smaxSolved ?? 0}
          scurrSolved={computedRatingStats?.scurrSolved ?? 0}
          hasSolvedProblems={(selectedProblemSetForStats?.solvedCount ?? 0) > 0}
        />
        <DownloadSolutionsDialog
          isOpen={isDownloadDialogOpen}
          onClose={() => {
            setIsDownloadDialogOpen(false);
            setSelectedProblemSetForDownload(null);
          }}
          problemSetId={selectedProblemSetForDownload?.problemSetId}
          rows={
            selectedProblemSetForDownload
              ? buildDownloadRows(
                  selectedProblemSetForDownload.problems.map((p) => ({
                    problemId: p.problemId,
                    name: p.name,
                    problemOrder: p.problemOrder,
                  })),
                  convertProblemItemsToAttempts(
                    selectedProblemSetForDownload.problems,
                  ),
                )
              : []
          }
        />
      </SignedIn>
    </>
  );
}
